import {
  ActionIcon, Badge, Button, Checkbox, Divider, Group, Loader, Paper, Radio, ScrollArea, Select,
  Slider, Stack, Table, Text, TextInput, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconChartLine, IconCube, IconId, IconLayersSubtract, IconRuler2, IconSearch, IconShape, IconZoomReset,
  IconSatellite,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Map as OlMap, Overlay, View } from "ol";
import { defaults as defaultControls, MousePosition, ScaleLine, ZoomToExtent } from "ol/control";
import { createStringXY } from "ol/coordinate";
import GeoJSON from "ol/format/GeoJSON";
import { LineString, MultiPolygon, Polygon } from "ol/geom";
import type { Geometry } from "ol/geom";
import Draw from "ol/interaction/Draw";
import type BaseLayer from "ol/layer/Base";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import { fromLonLat, toLonLat, transformExtent } from "ol/proj";
import TileWMS from "ol/source/TileWMS";
import VectorSource from "ol/source/Vector";
import { getArea, getLength } from "ol/sphere";
import { Fill, Stroke, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import { unByKey } from "ol/Observable";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { api, authFetch, unwrap } from "../api/client";
import { http } from "../api/http";
import { PointCloudViewer } from "../components/PointCloudViewer";
import { DockCard, MapDock } from "../components/MapDock";
import { useSentinel } from "../components/SentinelPanel";
import { TreeCardDrawer } from "../components/TreeCard";
import { fmtNum } from "../components/ui";
import { useCan } from "../auth/user";
import { useT } from "../i18n/script";
import { createLayer, type LayerDef } from "../map/layers";
import { NATIVE } from "../map/projections";

type Parcel = {
  id: number; ko_name: string | null; label: string; area_official: number | null; area_geom: number | null;
  land_use: string | null; ko_id: number | null;
};

type OrchardSel = {
  id: number; name: string; species: string; cultivar?: string | null; producer?: string | null; bpg?: string | null; opstina?: string | null;
  area_ha: number; tree_count: number; pcs: { id: number; survey_date: string; status: string; has_viewer: boolean }[];
};

/** Atributi objekta spoljnog sloja (WFS, uvezeni fajl, WMS GetFeatureInfo). */
type FeatInfo = { layer: string; items: Record<string, unknown>[] };

const RS_EXTENT = transformExtent([15.7, 42.5, 19.7, 45.3], "EPSG:4326", "EPSG:3857");
const highlightStyle = new Style({
  stroke: new Stroke({ color: "#00E5FF", width: 3 }),
  fill: new Fill({ color: "rgba(0,229,255,0.12)" }),
});
const measureStyle = new Style({
  stroke: new Stroke({ color: "#f39c12", width: 2.5, lineDash: [8, 6] }),
  fill: new Fill({ color: "rgba(243,156,18,0.15)" }),
});

/**
 * Mapa (OpenLayers) — koristi se kao kartica „Mapa“ u katalogu gazdinstava, kartonu gazdinstva i kartonu voćnjaka.
 * `orchardId` fokusira voćnjak (kartica Karton / 3D), `open3d` odmah otvara 3D, `fitExtent` (EPSG:3857) uokviruje gazdinstvo.
 */
export default function MapPage({ height, orchardId, open3d, fitExtent, compact, hideOrchardCard, results }: {
  height?: number | string; orchardId?: number; open3d?: boolean; fitExtent?: number[] | null; compact?: boolean; hideOrchardCard?: boolean;
  /** rezultat pretrage voćnjaka (GeoJSON, EPSG:4326) — prikazuje se istaknuto i mapa se uokviruje na njega */
  results?: { type: string; features: unknown[] } | null;
} = {}) {
  const { t } = useT();
  const can = useCan();
  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<OlMap | null>(null);
  const highlight = useRef(new VectorSource());
  const measureSrc = useRef(new VectorSource());
  const resultSrc = useRef(new VectorSource());
  const drawRef = useRef<Draw | null>(null);
  const [olLayers, setOlLayers] = useState<BaseLayer[]>([]);
  const [, force] = useState(0);
  const [selected, setSelected] = useState<Parcel | null>(null);
  const [panel] = useState(!compact);
  const [measure, setMeasure] = useState<"none" | "line" | "area">("none");
  const [koId, setKoId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [s2, setS2] = useState<{ date: string; mean: number | null }[] | null>(null);
  const [s2Loading, setS2Loading] = useState(false);
  const [orchard, setOrchard] = useState<OrchardSel | null>(null);
  const [view3d, setView3d] = useState<number | null>(null);
  const [tree, setTree] = useState<number | null>(null);
  const [featInfo, setFeatInfo] = useState<FeatInfo | null>(null);
  const [s2Open, setS2Open] = useState(false);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();

  const layersQ = useQuery({ queryKey: ["layers"], queryFn: async () => unwrap(await api.GET("/api/v1/layers")) });
  const kos = useQuery({
    queryKey: ["municipalities"], enabled: can("cadastre:read"),
    queryFn: async () => unwrap(await api.GET("/api/v1/cadastre/municipalities")),
  });

  // ------------------------------------------------------------ inicijalizacija mape
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = new OlMap({
      target: mapEl.current,
      view: new View({ center: fromLonLat([17.19, 44.772]), zoom: 13, maxZoom: 22, constrainResolution: false }),
      controls: defaultControls({ attributionOptions: { collapsible: true } }).extend([
        new ScaleLine({ units: "metric" }),
        new MousePosition({ projection: NATIVE, coordinateFormat: createStringXY(2), className: "ol-mouse-position" }),
        new ZoomToExtent({ extent: RS_EXTENT, label: "⌂", tipLabel: "Republika Srpska" }),
      ]),
    });
    map.addLayer(new VectorLayer({ source: highlight.current, style: highlightStyle, zIndex: 900 }));
    map.addLayer(new VectorLayer({ source: measureSrc.current, style: measureStyle, zIndex: 950 }));
    // rezultat pretrage: klik se tretira kao klik na voćnjak (sloj „vocnjaci“)
    map.addLayer(new VectorLayer({ source: resultSrc.current, zIndex: 880, properties: { code: "vocnjaci" },
      style: (f, res) => {
        const c = f.get("has_3d") ? "#2ecc71" : "#c8453a";
        // na sitnoj razmjeri poligoni su premali — voćnjak se crta kao tačka
        if (res > 12) return new Style({ geometry: (ft) => {
            const g = ft.getGeometry();
            return g instanceof MultiPolygon ? g.getInteriorPoints() : g instanceof Polygon ? g.getInteriorPoint() : g;
          },
          image: new CircleStyle({ radius: 6, fill: new Fill({ color: c }), stroke: new Stroke({ color: "#fff", width: 1.5 }) }) });
        return new Style({ stroke: new Stroke({ color: c, width: 2.5 }), fill: new Fill({ color: f.get("has_3d") ? "rgba(46,204,113,0.18)" : "rgba(200,69,58,0.16)" }) });
      } }));
    mapRef.current = map;
    return () => {
      // StrictMode (dev) montira dvaput — mapa se mora potpuno osloboditi i ponovo kreirati
      map.setTarget(undefined);
      mapRef.current = null;
    };
  }, []);

  // Sentinel-2 (slika vidljivog dijela, legenda, očitavanje) — dijelovi se smještaju u lijevu/desnu kolonu
  const sen = useSentinel(mapRef, s2Open, () => setS2Open(false), can("sentinel:analyze"));

  // ------------------------------------------------------------ slojevi iz kataloga (API)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !layersQ.data) return;
    olLayers.forEach((l) => map.removeLayer(l));
    const created = (layersQ.data as LayerDef[]).map(createLayer).filter(Boolean) as BaseLayer[];
    created.forEach((l) => map.addLayer(l));
    setOlLayers(created);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersQ.data]);

  // ------------------------------------------------------------ klik na mapu: stablo → karton, voćnjak → kartica (Karton / 3D), inače parcela
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const key = map.on("singleclick", async (e) => {
      if (drawRef.current || map.get("s2probe")) return;
      let treeId: number | null = null, orchardId: number | null = null;
      map.forEachFeatureAtPixel(e.pixel, (f, layer) => {
        const code = layer?.get("code");
        if (code === "stabla" && treeId == null) treeId = Number(f.getId() ?? f.get("id"));
        if (code === "vocnjaci" && orchardId == null) orchardId = Number(f.getId() ?? f.get("id"));
      }, { hitTolerance: 3 });
      if (treeId) {  // stablo: karton stabla + kartica voćnjaka (Karton / 3D) — u gustom zasadu klik skoro uvijek pogodi stablo
        setTree(treeId);
        if (orchardId) await selectOrchard(orchardId, false);
        return;
      }
      if (orchardId) { await selectOrchard(orchardId, false); return; }
      // spoljni vektorski slojevi (WFS, uvezeni fajlovi) — atributi objekta
      let vec: FeatInfo | null = null;
      map.forEachFeatureAtPixel(e.pixel, (f, layer) => {
        if (!layer?.get("featureInfo")) return;
        const { geometry: _g, ...props } = f.getProperties();
        if (!vec) vec = { layer: layer.get("title"), items: [] };
        if (vec.layer === layer.get("title") && vec.items.length < 5) vec.items.push(props);
      }, { hitTolerance: 4 });
      if (vec) { setFeatInfo(vec); return; }
      setFeatInfo(null);
      // WMS GetFeatureInfo za vidljive upitne spoljne slojeve (kroz serverski proksi)
      const view = map.getView();
      map.getAllLayers().filter((l) => l.get("queryable") && l.getVisible() && l.isVisible(view)).reverse()
        .slice(0, 3).forEach(async (l) => {
          const src = (l as TileLayer<TileWMS>).getSource();
          const url = src?.getFeatureInfoUrl?.(e.coordinate, view.getResolution()!, view.getProjection(),
            { INFO_FORMAT: "application/json", FEATURE_COUNT: 5 });
          if (!url) return;
          try {
            const r = await authFetch(url);
            if (!r.ok || !(r.headers.get("content-type") ?? "").includes("json")) return;
            const items = parseFeatureInfo(await r.text());
            if (items.length) setFeatInfo((cur) => cur ?? { layer: l.get("title"), items });
          } catch { /* servis ne podržava JSON GetFeatureInfo */ }
        });
      if (!can("cadastre:read")) return;
      const [lon, lat] = toLonLat(e.coordinate);
      const res = await api.GET("/api/v1/cadastre/parcels/at", { params: { query: { lon, lat } } });
      const p = res.data?.[0];
      setOrchard(null);
      if (p) await selectParcel(p.id, false);
      else { setSelected(null); highlight.current.clear(); }
    });
    return () => unByKey(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function selectOrchard(id: number, zoom = true) {
    try {
      const o = await http.get<OrchardSel & { geometry: unknown; bbox_3857: number[] }>(`/api/v1/orchards/${id}`);
      const pcs = can("pointcloud:read") ? await http.get<OrchardSel["pcs"]>(`/api/v1/orchards/${id}/pointclouds`) : [];
      setSelected(null);
      setOrchard({ ...o, pcs });
      highlight.current.clear();
      highlight.current.addFeature(new GeoJSON().readFeature({ type: "Feature", geometry: o.geometry, properties: {} },
        { featureProjection: "EPSG:3857" }) as never);
      if (zoom) mapRef.current?.getView().fit(o.bbox_3857, { padding: [80, 80, 80, 380], maxZoom: 19, duration: 400 });
      return pcs;
    } catch (err) {
      notifications.show({ color: "red", message: String(err) });
      return [];
    }
  }

  useEffect(() => {
    const map = mapRef.current;
    resultSrc.current.clear();
    if (!map || !results?.features?.length) return;
    resultSrc.current.addFeatures(new GeoJSON().readFeatures(results, { featureProjection: "EPSG:3857" }) as never);
    map.getView().fit(resultSrc.current.getExtent()!, { padding: [60, 60, 60, compact ? 60 : 380], maxZoom: 17, duration: 400 });
  }, [results, compact]);

  // otvaranje iz kartona voćnjaka: /mapa?vocnjak=ID&prikaz=3d
  useEffect(() => {
    const v = orchardId ?? Number(params.get("vocnjak"));
    if (!mapRef.current) return;
    if (!v) {
      if (fitExtent) mapRef.current.getView().fit(fitExtent, { padding: [60, 60, 60, compact ? 60 : 380], maxZoom: 18 });
      return;
    }
    (async () => {
      const pcs = await selectOrchard(v, true);
      const ready = pcs.find((p) => p.status === "ready" && p.has_viewer);
      if ((open3d || params.get("prikaz") === "3d") && ready) setView3d(ready.id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orchardId, open3d, fitExtent?.join(",")]);

  function close3d() {
    setView3d(null);
    if (params.get("prikaz")) { params.delete("prikaz"); setParams(params, { replace: true }); }
    // OpenLayers mora preračunati veličinu nakon što je bio sakriven
    setTimeout(() => mapRef.current?.updateSize(), 50);
  }

  async function selectParcel(id: number, zoom = true) {
    const d = unwrap(await api.GET("/api/v1/cadastre/parcels/{parcel_id}", {
      params: { path: { parcel_id: id }, query: { srid: 3857 } },
    }));
    setSelected(d as Parcel);
    setS2(null);
    highlight.current.clear();
    const f = new GeoJSON().readFeature({ type: "Feature", geometry: d.geometry, properties: {} });
    highlight.current.addFeature(f as never);
    if (zoom) mapRef.current?.getView().fit(d.bbox_3857 as number[], { padding: [80, 80, 80, 380], maxZoom: 18,
      duration: 500 });
  }

  async function search() {
    try {
      const res = unwrap(await api.GET("/api/v1/cadastre/parcels/search", {
        params: { query: { ko_id: koId ? Number(koId) : undefined, q: q || undefined, limit: 20 } },
      }));
      if (!res.length) notifications.show({ color: "yellow", message: t("Parcela nije pronađena") });
      else await selectParcel(res[0].id);
    } catch (e) {
      notifications.show({ color: "red", message: String(e) });
    }
  }

  async function runSentinel() {
    if (!selected) return;
    setS2Loading(true);
    try {
      const end = new Date();
      const start = new Date(end.getTime() - 180 * 86400_000);
      const r = await http.post<{ series: Record<string, { date: string; mean: number | null }[]> }>("/api/v1/sentinel/stats", {
        target_type: "cadastral_parcel", target_id: selected.id, index_codes: ["NDVI"],
        date_from: start.toISOString().slice(0, 10), date_to: end.toISOString().slice(0, 10),
        aggregation: "P10D", max_cloud_coverage: 40, save: true, use_cache: true,
      });
      setS2(r.series?.NDVI ?? []);
    } catch (e) {
      notifications.show({ color: "red", title: "Sentinel-2", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setS2Loading(false);
    }
  }

  // ------------------------------------------------------------ mjerenje (FT-02, FT-08)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (drawRef.current) { map.removeInteraction(drawRef.current); drawRef.current = null; }
    map.getOverlays().getArray().filter((o) => o.get("measure")).forEach((o) => map.removeOverlay(o));
    measureSrc.current.clear();
    if (measure === "none") return;
    const draw = new Draw({ source: measureSrc.current, type: measure === "line" ? "LineString" : "Polygon",
      style: measureStyle });
    let tipEl: HTMLDivElement | null = null;
    let overlay: Overlay | null = null;
    draw.on("drawstart", (ev) => {
      measureSrc.current.clear();
      map.getOverlays().getArray().filter((o) => o.get("measure")).forEach((o) => map.removeOverlay(o));
      tipEl = document.createElement("div");
      tipEl.className = "measure-tip";
      overlay = new Overlay({ element: tipEl, offset: [0, -12], positioning: "bottom-center" });
      overlay.set("measure", true);
      map.addOverlay(overlay);
      ev.feature.getGeometry()!.on("change", (ce) => {
        const g = ce.target as Geometry;
        let txt = "";
        if (g instanceof LineString) {
          const l = getLength(g);
          txt = l > 1000 ? `${(l / 1000).toFixed(3)} km` : `${l.toFixed(2)} m`;
          overlay!.setPosition(g.getLastCoordinate());
        } else if (g instanceof Polygon) {
          const a = getArea(g);
          txt = a > 10000 ? `${(a / 10000).toFixed(4)} ha` : `${a.toFixed(1)} m²`;
          overlay!.setPosition(g.getInteriorPoint().getCoordinates());
        }
        tipEl!.innerText = txt;
      });
    });
    map.addInteraction(draw);
    drawRef.current = draw;
  }, [measure]);

  const groups = useMemo(() => {
    const g: Record<string, BaseLayer[]> = {};
    olLayers.filter((l) => !l.get("basemap")).forEach((l) => (g[l.get("group")] ??= []).push(l));
    return g;
  }, [olLayers]);
  const basemaps = olLayers.filter((l) => l.get("basemap"));
  const activeBase = basemaps.find((b) => b.getVisible())?.get("code") ?? "";

  return (
    <div style={{ position: "relative", height: height ?? "calc(100dvh - var(--app-shell-header-height, 56px))",
      borderRadius: height ? 8 : 0, overflow: "hidden" }}>
      <div ref={mapEl} className="map-root" />

      {/* ---------------- lijeva kolona: slojevi i pretraga, atributi objekta, legenda snimka (bez preklapanja) */}
      <MapDock side="left">
        <DockCard id="layers" grow startCollapsed={!panel} icon={<IconLayersSubtract size={16} />} title={t("Slojevi i pretraga parcela")}>
          {can("cadastre:read") && (
            <Stack gap={6}>
              <Select size="xs" searchable clearable placeholder={t("Katastarska opština")} value={koId}
                onChange={setKoId}
                data={(kos.data ?? []).map((k) => ({ value: String(k.id), label: `${k.name} (${k.parcel_count})` }))} />
              <Group gap={6} wrap="nowrap">
                <TextInput size="xs" style={{ flex: 1 }} placeholder={t("Broj parcele, npr. 111/4")} value={q}
                  onChange={(e) => setQ(e.currentTarget.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
                <ActionIcon size="lg" onClick={search} aria-label="search"><IconSearch size={16} /></ActionIcon>
              </Group>
            </Stack>
          )}
          <Divider my="sm" label={t("Slojevi")} labelPosition="left" />
          <ScrollArea type="auto" style={{ flex: 1, minHeight: 80 }} offsetScrollbars>
            <Radio.Group value={activeBase} onChange={(code) => {
              basemaps.forEach((b) => b.setVisible(b.get("code") === code)); force((x) => x + 1);
            }}>
              <Stack gap={4} mb="sm">
                {basemaps.map((b) => <Radio key={b.get("code")} size="xs" value={b.get("code")} label={t(b.get("title"))} />)}
              </Stack>
            </Radio.Group>
            {Object.entries(groups).map(([g, ls]) => (
              <Stack key={g} gap={4} mb="sm">
                <Text size="xs" fw={700} c="dimmed" tt="uppercase">{t(g)}</Text>
                {ls.map((l) => (
                  <div key={l.get("code")}>
                    <Checkbox size="xs" checked={l.getVisible()} label={t(l.get("title"))}
                      title={l.get("description") ?? undefined}
                      onChange={(e) => { l.setVisible(e.currentTarget.checked); force((x) => x + 1); }} />
                    {l.getVisible() && (
                      <Slider size="xs" ml={28} mt={4} mb={6} min={0} max={1} step={0.05} value={l.getOpacity()}
                        label={(v) => `${Math.round(v * 100)}%`}
                        onChange={(v) => { l.setOpacity(v); force((x) => x + 1); }} />
                    )}
                  </div>
                ))}
              </Stack>
            ))}
          </ScrollArea>
        </DockCard>
      {featInfo && (
        <DockCard id={`feat-${featInfo.layer}`} label={t("Objekat sloja")} title={t(featInfo.layer)} onClose={() => setFeatInfo(null)}>
          <ScrollArea.Autosize mah={260} type="auto" mt={6}>
            {featInfo.items.map((p, i) => (
              <Table key={i} withRowBorders={false} verticalSpacing={1} fz="xs" mb={i < featInfo.items.length - 1 ? "xs" : 0}>
                <Table.Tbody>
                  {Object.entries(p).filter(([, v]) => v !== null && v !== "" && typeof v !== "object").slice(0, 30).map(([k, v]) => (
                    <Table.Tr key={k}><Table.Td c="dimmed" style={{ whiteSpace: "nowrap" }}>{k}</Table.Td>
                      <Table.Td style={{ wordBreak: "break-word" }}>{String(v)}</Table.Td></Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ))}
          </ScrollArea.Autosize>
        </DockCard>
      )}
      {sen.legend && <div style={{ marginTop: "auto" }}>{sen.legend}</div>}
      </MapDock>

      {/* ---------------- alati */}
      <Paper className="floating-panel" shadow="md" p={4} top={12} right={12} radius="md">
        <Group gap={4}>
          <Tooltip label={t("Mjerenje udaljenosti")}>
            <ActionIcon variant={measure === "line" ? "filled" : "subtle"} onClick={() => setMeasure(measure === "line" ? "none" : "line")}>
              <IconRuler2 size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t("Mjerenje površine")}>
            <ActionIcon variant={measure === "area" ? "filled" : "subtle"} onClick={() => setMeasure(measure === "area" ? "none" : "area")}>
              <IconShape size={18} />
            </ActionIcon>
          </Tooltip>
          {can("sentinel:analyze") && (
            <Tooltip label={t("Sentinel-2 snimak: prirodne boje, kompoziti i indeksi za vidljivi dio karte")}>
              <ActionIcon variant={s2Open ? "filled" : "subtle"} onClick={() => setS2Open(!s2Open)}><IconSatellite size={18} /></ActionIcon>
            </Tooltip>
          )}
          <Tooltip label={t("Očisti")}>
            <ActionIcon variant="subtle" color="gray" onClick={() => { setMeasure("none"); highlight.current.clear(); setSelected(null); setFeatInfo(null); }}>
              <IconZoomReset size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Paper>

      {/* ---------------- desna kolona: Sentinel, očitavanje, parcela, voćnjak (jedno ispod drugog) */}
      <MapDock side="right">
      {sen.controls}
      {sen.result}
      {selected && (
        <DockCard id="parcel" label={t("Katastarska parcela")} title={`${selected.label} · ${selected.ko_name ?? ""}`}
          onClose={() => { setSelected(null); highlight.current.clear(); }}>
          <Table mt={6} withRowBorders={false} verticalSpacing={2} fz="sm">
            <Table.Tbody>
              <Table.Tr><Table.Td c="dimmed">{t("KO")}</Table.Td><Table.Td>{selected.ko_name}</Table.Td></Table.Tr>
              <Table.Tr><Table.Td c="dimmed">{t("Površina (katastar)")}</Table.Td>
                <Table.Td>{selected.area_official?.toLocaleString("sr")} m²</Table.Td></Table.Tr>
              <Table.Tr><Table.Td c="dimmed">{t("Površina (geometrija)")}</Table.Td>
                <Table.Td>{selected.area_geom?.toLocaleString("sr")} m²</Table.Td></Table.Tr>
              <Table.Tr><Table.Td c="dimmed">{t("Namjena")}</Table.Td><Table.Td>{selected.land_use ?? "—"}</Table.Td></Table.Tr>
              <Table.Tr><Table.Td c="dimmed">ID</Table.Td><Table.Td>{selected.id}</Table.Td></Table.Tr>
            </Table.Tbody>
          </Table>
          {can("orchards:read") && <ParcelOrchards parcelId={selected.id} onOpen={(id) => nav(`/vocnjaci/${id}`)}
            onSelect={(id) => selectOrchard(id, true)} />}
          {can("sentinel:analyze") && (
            <Button mt="sm" size="xs" fullWidth variant="light" leftSection={<IconChartLine size={16} />}
              loading={s2Loading} onClick={runSentinel}>
              {t("Sentinel-2 NDVI (zadnjih 6 mjeseci)")}
            </Button>
          )}
          {s2 && (s2.length === 0 ? <Text size="xs" c="dimmed" mt="xs">{t("Nema snimaka bez oblaka u periodu")}</Text> : (
            <Sparkline data={s2} />
          ))}
        </DockCard>
      )}
      {/* ---------------- voćnjak: karton i 3D */}
      {orchard && !view3d && !hideOrchardCard && (
        <DockCard id="orchard" label={t("Voćnjak")} title={orchard.name} onClose={() => { setOrchard(null); highlight.current.clear(); }}>
          <Text size="xs" c="dimmed" mt={4} mb={6}>{orchard.producer ?? "—"}{orchard.bpg ? ` · BPG ${orchard.bpg}` : ""} · {t(orchard.opstina ?? "")}</Text>
          <Group gap={6} mb="sm">
            <Badge variant="light">{orchard.species}{orchard.cultivar ? ` · ${orchard.cultivar}` : ""}</Badge>
            <Badge variant="outline" color="gray">{fmtNum(orchard.area_ha, 2)} ha</Badge>
            <Badge variant="outline" color="gray">{fmtNum(orchard.tree_count, 0)} {t("stabala")}</Badge>
          </Group>
          <Group grow gap="xs">
            {orchard.id !== orchardId && <Button size="xs" variant="light" leftSection={<IconId size={16} />} onClick={() => nav(`/vocnjaci/${orchard.id}`)}>{t("Karton")}</Button>}
            {can("pointcloud:read") && (() => {
              const ready = orchard.pcs.find((p) => p.status === "ready" && p.has_viewer);
              return (
                <Tooltip label={ready ? `${t("3D snimak od")} ${ready.survey_date}` : t("Voćnjak nema obrađen 3D snimak")}>
                  <Button size="xs" leftSection={<IconCube size={16} />} disabled={!ready} onClick={() => ready && setView3d(ready.id)}>3D</Button>
                </Tooltip>
              );
            })()}
          </Group>
        </DockCard>
      )}
      </MapDock>

      {view3d && (
        <div style={{ position: "absolute", inset: 0, zIndex: 20 }}>
          <PointCloudViewer pcId={view3d} onClose={close3d} onTree={(id) => setTree(id)} selectedTree={tree} />
          {orchard && (
            <Paper shadow="md" px="sm" py={6} style={{ position: "absolute", top: 12, right: 12, zIndex: 3 }}>
              <Text size="sm" fw={600}>{orchard.name}</Text>
              <Text size="xs" c="dimmed">{orchard.producer ?? ""} · {fmtNum(orchard.area_ha, 2)} ha</Text>
            </Paper>
          )}
        </div>
      )}
      <TreeCardDrawer treeId={tree} onClose={() => setTree(null)} />

      {layersQ.isLoading && <Loader className="floating-panel" style={{ top: "50%", left: "50%" }} />}
    </div>
  );
}

/**
 * Atributi iz WMS GetFeatureInfo (application/json). GeoServer za grupe slojeva ponekad vrati više spojenih
 * FeatureCollection objekata (neispravan JSON) — tada se izdvajaju pojedinačni blokovi `properties`.
 */
function parseFeatureInfo(txt: string): Record<string, unknown>[] {
  try {
    const j = JSON.parse(txt) as { features?: { properties?: Record<string, unknown> }[] };
    return (j.features ?? []).map((f) => f.properties ?? {}).filter((p) => Object.keys(p).length);
  } catch {
    const out: Record<string, unknown>[] = [];
    for (const m of txt.matchAll(/"properties"\s*:\s*(\{[^{}]*\})/g)) {
      try {
        const p = JSON.parse(m[1]) as Record<string, unknown>;
        // preskače "crs": {"properties": {"name": "EPSG:..."}}
        if (Object.keys(p).length && !(Object.keys(p).length === 1 && "name" in p && /^EPSG:/.test(String(p.name)))) out.push(p);
      } catch { /* blok nije JSON */ }
    }
    return out.slice(0, 10);
  }
}

/** Voćnjaci na izabranoj katastarskoj parceli — ulaz u karton voćnjaka sa parcele. */
function ParcelOrchards({ parcelId, onOpen, onSelect }: { parcelId: number; onOpen: (id: number) => void; onSelect: (id: number) => void }) {
  const { t } = useT();
  const q = useQuery({ queryKey: ["parcel-orchards", parcelId],
    queryFn: () => http.get<{ features: { id: number; properties: { name: string; producer: string | null; area_ha: number; has_3d: boolean } }[] }>(
      "/api/v1/orchards/geojson", { parcel_id: parcelId }) });
  const f = q.data?.features ?? [];
  if (!f.length) return <Text size="xs" c="dimmed" mt="xs">{q.isLoading ? t("Provjera voćnjaka…") : t("Na parceli nema voćnjaka u vašoj nadležnosti.")}</Text>;
  return (
    <Stack gap={6} mt="sm">
      <Text size="xs" fw={700} c="dimmed">{t("Voćnjaci na parceli")}</Text>
      {f.map((x) => (
        <Group key={x.id} justify="space-between" wrap="nowrap" gap={6}>
          <Text size="sm" lineClamp={1} style={{ cursor: "pointer" }} onClick={() => onSelect(x.id)}>
            {x.properties.name}{x.properties.has_3d ? " · 3D" : ""}
          </Text>
          <Button size="compact-xs" variant="light" onClick={() => onOpen(x.id)}>{t("Karton")}</Button>
        </Group>
      ))}
    </Stack>
  );
}

/** Mala vremenska serija bez dodatnih biblioteka (SVG). */
function Sparkline({ data }: { data: { date: string; mean: number | null }[] }) {
  const pts = data.filter((d) => d.mean != null) as { date: string; mean: number }[];
  if (!pts.length) return null;
  const W = 300, H = 90, P = 6;
  const min = Math.min(0, ...pts.map((p) => p.mean)), max = Math.max(1, ...pts.map((p) => p.mean));
  const x = (i: number) => P + (i * (W - 2 * P)) / Math.max(1, pts.length - 1);
  const y = (v: number) => H - P - ((v - min) / (max - min)) * (H - 2 * P);
  const path = pts.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.mean).toFixed(1)}`).join(" ");
  return (
    <Stack gap={2} mt="sm">
      <Group justify="space-between">
        <Text size="xs" c="dimmed">NDVI</Text>
        <Badge size="xs" variant="light">{pts[pts.length - 1].mean.toFixed(2)}</Badge>
      </Group>
      <svg width={W} height={H} role="img" aria-label="NDVI">
        <rect x={0} y={0} width={W} height={H} rx={6} fill="#f4f8f4" />
        <path d={path} fill="none" stroke="#2e7d32" strokeWidth={2} />
        {pts.map((p, i) => <circle key={p.date} cx={x(i)} cy={y(p.mean)} r={2.5} fill="#2e7d32"><title>{`${p.date}: ${p.mean.toFixed(3)}`}</title></circle>)}
      </svg>
      <Group justify="space-between"><Text size="10px" c="dimmed">{pts[0].date}</Text>
        <Text size="10px" c="dimmed">{pts[pts.length - 1].date}</Text></Group>
    </Stack>
  );
}
